import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { buildOrderViews, itemFromInput, PosClient, uuid, type OrderView, type ViewItem } from "@bb/client-core";
import type { NewItemInput } from "@bb/protocol";

export const client = new PosClient(import.meta.env.VITE_API_URL ?? "");
void client.init();

export function usePos() {
  return useSyncExternalStore(client.subscribe, client.getSnapshot);
}

export function useOrderViews(): OrderView[] {
  const s = usePos();
  return useMemo(() => buildOrderViews(s.orders, s.pending, s.catalog), [s.orders, s.pending, s.catalog]);
}

export { uuid };

/* ─── instalacja PWA ─── */

type InstallEvent = Event & { prompt: () => Promise<void> };
let deferred: InstallEvent | null = null;
const installListeners = new Set<() => void>();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as InstallEvent;
    installListeners.forEach((f) => f());
  });
}

/** Funkcja „zainstaluj”, gdy przeglądarka na to pozwala (Chrome na Androidzie); inaczej null. */
export function useInstallPrompt(): (() => void) | null {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((x) => x + 1);
    installListeners.add(f);
    return () => void installListeners.delete(f);
  }, []);
  return deferred
    ? () => {
        void deferred!.prompt();
        deferred = null;
        installListeners.forEach((f) => f());
      }
    : null;
}

/* ─── koszyk: pozycje jeszcze niezapisane (edytowalne bez wysyłania) ─── */

export interface CartLine extends Omit<NewItemInput, "modifiers"> {
  modifiers: { modifierId: string; qty: number }[];
  /** Kto nabił — tylko na tym urządzeniu (sala pokazuje to przy stoliku), nie idzie na serwer. */
  by?: string;
}

const CART_KEY = "bb.cart";

function readCart(scope: string): CartLine[] {
  try {
    return JSON.parse(localStorage.getItem(`${CART_KEY}.${scope}`) ?? "[]");
  } catch {
    return [];
  }
}

function writeCart(scope: string, lines: CartLine[]) {
  try {
    if (lines.length) localStorage.setItem(`${CART_KEY}.${scope}`, JSON.stringify(lines));
    else localStorage.removeItem(`${CART_KEY}.${scope}`);
  } catch {
    // pełna pamięć / tryb prywatny — koszyk zostaje tylko w pamięci
  }
}

/** Podgląd koszyka bez otwierania rachunku (sala: stolik z pozycjami nabitymi, ale niewysłanymi). */
export const peekCart = (scope: string): CartLine[] => readCart(scope);

/** Koszyk trzymany w localStorage (osobno dla każdego rachunku) — przeładowanie strony nie kasuje nabijanych pozycji. */
export function useCart(scope: string) {
  const [state, setState] = useState(() => ({ scope, lines: readCart(scope) }));
  let current = state;
  if (state.scope !== scope) {
    current = { scope, lines: readCart(scope) };
    setState(current);
  }
  const lines = current.lines;
  // Zapis synchroniczny w chwili zmiany — przełączenie rachunku zaraz potem niczego nie gubi.
  const setLines = (fn: (cur: CartLine[]) => CartLine[]) =>
    setState((s) => {
      const next = fn(s.lines);
      writeCart(s.scope, next);
      return { scope: s.scope, lines: next };
    });

  return {
    lines,
    add(line: Omit<CartLine, "itemId">) {
      setLines((cur) => {
        const plain = line.modifiers.length === 0 && !line.notes;
        const same = plain && cur.find((l) => l.productId === line.productId && l.variantId === line.variantId && l.modifiers.length === 0 && !l.notes);
        if (same) return cur.map((l) => (l === same ? { ...l, qty: l.qty + line.qty } : l));
        return [...cur, { ...line, itemId: uuid() }];
      });
    },
    update(itemId: string, patch: Partial<CartLine>) {
      setLines((cur) => cur.map((l) => (l.itemId === itemId ? { ...l, ...patch } : l)));
    },
    remove(itemId: string) {
      setLines((cur) => cur.filter((l) => l.itemId !== itemId));
    },
    /** Przywraca pozycję w dawnym miejscu (cofnięcie usunięcia albo zmniejszenia ilości). */
    put(line: CartLine, index: number) {
      setLines((cur) => {
        if (cur.some((l) => l.itemId === line.itemId)) return cur.map((l) => (l.itemId === line.itemId ? line : l));
        const next = [...cur];
        next.splice(Math.min(index, next.length), 0, line);
        return next;
      });
    },
    clear() {
      setLines(() => []);
    },
  };
}

export type Cart = ReturnType<typeof useCart>;

export function cartItems(lines: CartLine[], catalog: Parameters<typeof itemFromInput>[1]): (ViewItem & { cart: true })[] {
  return lines.map((l) => ({ ...itemFromInput(l, catalog), stage: "device" as const, cart: true as const }));
}

/** Wylogowanie po bezczynności (wspólne tablety). */
export function useIdleLock(minutes: number, active: boolean) {
  useEffect(() => {
    if (!active) return;
    let timer = setTimeout(() => void client.lock(), minutes * 60_000);
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => void client.lock(), minutes * 60_000);
    };
    window.addEventListener("pointerdown", reset);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("pointerdown", reset);
    };
  }, [minutes, active]);
}
